import {Component, OnInit} from '@angular/core';
import {GapService} from '../../api/services/gap/gap.service';
import {GlobalService} from '../../api/services/global/global.service';
import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {GapPremiumsRequest} from '../../api/models/gap-premiums-request';
import {BlockUI, NgBlockUI} from 'ng-block-ui';

declare var jcf: any;
declare let jQuery: any;

@Component({
  selector: 'app-view-quotes',
  templateUrl: './create-quote.component.html',
  styleUrls: ['./create-quote.component.css']
})
export class CreateQuoteComponent implements OnInit {
  @BlockUI() blockUI: NgBlockUI;
  // Commons
  leadId = '';
  monthsToDisplay: string[] = [];
  serviceError: boolean = false;
  errorMessage: string;
  effectiveDateError: boolean = false;
  smokerStatusError: boolean = false;

  // SIB
  inclSIB: string = 'YES';
  exclSIB: string = 'NO';
  isAgeOver60: boolean = false;

  constructor(private router: Router,
              private gapService: GapService,
              protected globalService: GlobalService) {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        setTimeout(() => {
          this.jcfReload();
        }, 50);
      });
  }

  ngOnInit() {
    this.globalService.scrollToTop();
    this.leadId = this.globalService.leadId;

    this.globalService.effectiveDate = null;
    this.globalService.includedSIB = false;
    this.globalService.memberSmoker = null;
    this.globalService.spouseSmoker = null;
    this.globalService.gapPremiumsResponse = null;

    this.effectiveDateError = false;
    this.smokerStatusError = false;
    this.serviceError = false;

    this.commencementDate();
    this.retrieveHealthPolicyMembers();
  }

  private retrieveHealthPolicyMembers(): void {
    this.globalService.mainMember = this.globalService.healthPolicyMembers.members.find(mainMember => mainMember.role === 'PP');
    this.globalService.spouses = this.globalService.healthPolicyMembers.members.filter(memberRole => memberRole.role === 'SP');
    if (this.globalService.spouses.length > 0) {
      this.globalService.firstSpouse = this.globalService.spouses.find(spouse => spouse.role === 'SP');
    }
    this.globalService.adults = this.globalService.healthPolicyMembers.members.filter(memberRole => memberRole.role === 'AD');
    this.globalService.children = this.globalService.healthPolicyMembers.members.filter(memberRole => memberRole.role === 'CH');

    if (this.globalService.calculateAge(this.globalService.healthPolicyMembers.oldestDob) >= 60){
      this.isAgeOver60 = true;
      this.globalService.includedSIB = false;
    } else {
      this.isAgeOver60 = false;
    }
  }

  private commencementDate(): void {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    const currentDate = new Date();
    let remainingMonths: string[] = months.filter((month, index) => index >= currentDate.getMonth());
    if (currentDate.getDate() < 20) {
      for (const month of remainingMonths) {
        const monthToDisplay = '01' + ' ' + month + ' ' + currentDate.getFullYear().toString();
        this.monthsToDisplay.push(monthToDisplay);
      }
    } else if (currentDate.getDate() >= 20) {
      const removedCurrentMonth: string = remainingMonths.shift();
      for (const month of remainingMonths) {
        const monthToDisplay = '01' + ' ' + month + ' ' + currentDate.getFullYear().toString();
        this.monthsToDisplay.push(monthToDisplay);
      }
    }
  }

  protected sibSelection(event: any): void {
    this.globalService.includedSIB = event.target.value === 'YES';

    this.smokerStatusError = false;

    if (!this.globalService.includedSIB) {
      this.globalService.memberSmoker = null;
      this.globalService.spouseSmoker = null;
    }

    this.jcfReload();
  }

  protected effectiveDateSelection(event: any): void {
    this.globalService.effectiveDate = event.target.value;
    this.effectiveDateError = false;
  }

  protected memberSmokerStatusSelection(event: any): void {
    this.globalService.memberSmoker = event.target.value;
    this.smokerStatusError = false;
    this.jcfReload();
  }

  protected spouseSmokerStatusSelection(event: any): void {
    this.globalService.spouseSmoker = event.target.value;
    this.smokerStatusError = false;
    this.jcfReload();
  }

  private getPremiumsRequest(): GapPremiumsRequest {
    const totalAdults: number = this.globalService.adults.length + this.globalService.spouses.length;
    return {
      medicalSchemePlan: this.globalService.healthPolicyMembers.mainMemberSchemePlan,
      memberSmokerStatus: this.globalService.memberSmoker,
      spouseSmokerStatus: this.globalService.spouses.length > 0 ? this.globalService.spouseSmoker : 'No spouse',
      numAdultDependents: totalAdults.toString(),
      numChildDependents: this.globalService.children.length.toString(),
      effectiveDate: this.globalService.effectiveDate,
      sibIncluded: this.globalService.includedSIB,
      nameAndRole: this.globalService.healthPolicyMembers.members
    };
  }

  protected calculatePremiums(): void {
    this.effectiveDateError = false;
    this.smokerStatusError = false;

    if (!this.globalService.effectiveDate) {
      this.effectiveDateError = true;
      return;
    }

    if (this.globalService.includedSIB) {
      const isMemberSmokerSet = this.globalService.memberSmoker === 'Smoker' || this.globalService.memberSmoker === 'Non-smoker';

      const isSpouseSmokerSet = this.globalService.spouses.length === 0 || (this.globalService.spouseSmoker === 'Smoker' || this.globalService.spouseSmoker === 'Non-smoker');

      if (!isMemberSmokerSet || !isSpouseSmokerSet){
        this.smokerStatusError = true;
        return;
      }
    }

    this.blockUI.start();
    this.gapService.getGapPremiums(this.getPremiumsRequest()).subscribe(response => {
      if (response) {
        this.globalService.gapPremiumsResponse = response;
        this.router.navigateByUrl('view-premium').then(navigated => {
          if (navigated) {
            this.blockUI.stop();
          }
        });
      }
    },
      () => {
      this.blockUI.stop();
      this.serviceError = true;
      this.errorMessage = 'Unable to calculate premiums, please try again later.';
      });
  }

  private jcfReload(): void {
      jQuery(function (): void {
        // @ts-ignore
        jcf.replaceAll();
      });
  }
}
